import { useRef, type CSSProperties, type ReactNode } from "react";
import { Autocomplete } from "@react-google-maps/api";
import { Input } from "semantic-ui-react";
import Header from "semantic-ui-react/dist/commonjs/elements/Header";
import "semantic-ui-css/components/input.min.css";
import "semantic-ui-css/components/header.min.css";
import styles from "./InputWithLabel.module.css";
import NumericInput from "@components/AutoApply/CreateAutoApplySubscription/NumericInput";
import { getSelectedAddress } from './selectedAddress';

type InputWithLabelProps = {
  placeholder?: string;
  explanationStr?: string;
  label?: ReactNode;
  errorText?: string;
  onChange?: (event: any) => void;
  onFocus?: (event: any) => void;
  name?: string;
  normal?: boolean;
  value?: any;
  disabled?: boolean;
  password?: boolean;
  showError?: boolean;
  labelSize?: any;
  labelStyle?: CSSProperties;
  labelContainerStyle?: CSSProperties;
  style?: CSSProperties;
  check?: boolean;
  numeric?: boolean;
  required?: boolean;
  onSelected?: (value: string) => void;
  requiredStyle?: CSSProperties;
  [key: string]: any;
};

export default function InputWithLabel({
  id,
  placeholder,
  explanationStr,
  label,
  errorText,
  onChange,
  onFocus,
  name,
  normal,
  value,
  disabled,
  password,
  showError,
  labelSize = "h4",
  labelStyle,
  labelContainerStyle,
  style,
  check,
  numeric,
  required,
  onSelected,
  requiredStyle = {},
  ...props
}: InputWithLabelProps) {
  const hasError = (errorText && errorText.length) || showError ? true : false;
  const autocompleteRef = useRef(null);

  const handlePlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      const fullAddress = getSelectedAddress(place);

      if (fullAddress) {
        onChange({ target: { name, value: fullAddress } });
        if (onSelected) {
          onSelected(fullAddress);
        }
      }
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !value) {
      event.preventDefault();  // Prevent the form submission or any default action
    }
  };

  const renderLabel = () => {
    if (label) {
      return (
        <div className={styles.labelContainer} style={labelContainerStyle}>
          {!normal && <Header data-ui="field-label" className={styles.label} as={id ? 'label' : labelSize} htmlFor={id} style={labelStyle}>
            {label}{required ? <span style={requiredStyle} className="text-red-600">&nbsp;&nbsp;*</span> : null}
          </Header>}
          {normal && (
            <label data-ui="field-label" htmlFor={id} style={labelStyle}>
            {label}{required ? <span style={requiredStyle} className="text-red-600">&nbsp;&nbsp;*</span> : null}
            </label>
          )}
          {explanationStr ? (
            <div className={styles.explanation}>{explanationStr}</div>
          ) : null}
          {errorText ? (
            <div className={styles.errorText}>*{errorText}</div>
          ) : null}
        </div>
      );
    }
    return null;
  };

  if (numeric) {
    return (
      <div className={styles.container} style={style}>
        {renderLabel()}
        <NumericInput
          id={id}
          type={"text"}
          error={hasError}
          name={name}
          fluid
          placeholder={placeholder}
          onChange={onChange}
          onKeyPress={handleKeyPress} // Add key press handler
          value={value}
          disabled={disabled}
          icon={check ? { name: "checkmark", circular: false, color: "green" } : null}
          onFocus={onFocus}
          {...props}
        />        
      </div>
    )
  }

  return (
    <div className={styles.container} style={style}>
      {renderLabel()}
      {name === "address" ? (
        <Autocomplete
          onLoad={(autocomplete) => {
            autocompleteRef.current = autocomplete;
          }}
          onPlaceChanged={handlePlaceChanged}
          options={{ types: ["address"] }}
        >
          <Input
            id={id}
            type={password ? "password" : "text"}
            error={hasError}
            name={name}
            fluid
            placeholder={placeholder}
            onChange={onChange} // Allows manual typing
            onKeyPress={handleKeyPress} // Add key press handler
            value={value}
            disabled={disabled}
            icon={check ? { name: "checkmark", circular: false, color: "green" } : null}
            onFocus={onFocus}
            {...props}
          />
        </Autocomplete>
      ) : (
        <Input
          id={id}
          type={password ? "password" : "text"}
          error={hasError}
          name={name}
          fluid
          placeholder={placeholder}
          onChange={onChange}
          value={value}
          disabled={disabled}
          icon={check ? { name: "checkmark", circular: false, color: "green" } : null}
          onFocus={onFocus}
          {...props}
        />
      )}
    </div>
  );
}
